import Button from "@/lib/Button.js";
import { useConfirmCostEstimates } from "@/lib/queries/hooks";
import { useParams } from "react-router";
import useToPage from "@/lib/useToPage";
import PartStatusIcon from "@/lib/PartStatusIcon";
import { RequestedPartDetail } from "@/lib/queries/types";
import ArrowLeftIcon from "~icons/local/arrow-left";
import { flatParts, readyToQuote } from "@/lib/utils";
import { components } from "@/lib/ApiSchema";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import { Tooltip } from "react-tooltip";
import useModal from "@/lib/modal/useModal";
import AutoCompleteSubPartsModal from "./AutoCompleteSubPartsModal";
import { useState } from "react";

function areAllChildEstimatesCompletable(
  part: components["schemas"]["RequestedPartWithOperationsSchema"],
) {
  return (
    part.children.length > 0 &&
    part.children.some((subPart) => subPart.will_bid === null) &&
    part.children.flatMap((subPart) =>
      subPart.operations.filter(
        ({ validation_errors }) => Object.keys(validation_errors).length,
      ),
    ).length === 0
  );
}

export default function BottomBar({
  part,
}: {
  part: components["schemas"]["RequestedPartWithOperationsSchema"];
}) {
  const tooltipId = Math.random().toString();

  const [activeButton, setActiveButton] = useState<null | "noBid" | "complete">(
    null,
  );

  const { toPart, toQuote } = useToPage();
  const { updatePart, isPending } = useUpdatePart();
  const { confirmCostEstimates } = useConfirmCostEstimates();
  const { partId } = useParams();
  const quoteRevision = useGetCurrentQuoteRevision();
  const { showModal, closeModal } = useModal();

  if (!quoteRevision) return null;
  const operations = part.operations;
  const canEditQuoteRevision = quoteRevision.can_edit;

  const parts = flatParts(quoteRevision.requested_part_revisions);
  const partIndex = parts.findIndex((p) => p.id === partId);
  const draftCount = parts.reduce(
    (count, part) => count + (part.will_bid === null ? 1 : 0),
    0,
  );
  const willBid = part.will_bid;
  const operationsWithError = operations.filter(
    ({ validation_errors }) => Object.keys(validation_errors).length,
  );

  return (
    <div className="w-full flex gap-2 justify-between items-center px-3 pb-1">
      <div className="w-[200px] text-sm flex gap-1 items-center">
        <ArrowLeftIcon
          onClick={toNextPart}
          className="cursor-pointer size-4 text-slate-400 hover:text-slate-600"
        />
        <div className="text-gray-400">
          Part {partIndex + 1} / {parts.length}
        </div>
        <ArrowLeftIcon
          onClick={toPreviousPart}
          className="cursor-pointer size-4 rotate-180 text-slate-400 hover:text-slate-600"
        />
      </div>
      <div className="grow flex justify-center gap-2 items-center">
        {willBid !== null && (
          <>
            <PartStatusIcon willBid={willBid} />
            <div>{willBidToText(willBid)}</div>
          </>
        )}
      </div>
      {canEditQuoteRevision && (
        <div className="w-[200px] flex gap-4 justify-end items-center">
          {willBid === null ? (
            <>
              <Button
                alert
                disabled={isPending}
                loading={activeButton === "noBid" && isPending}
                onClick={() => {
                  setActiveButton("noBid");
                  updatePart({
                    payload: { will_bid: false },
                    onSuccess: () => {
                      setActiveButton(null);
                      toNextDraftPart();
                    },
                  });
                }}
              >
                <div className="flex items-center gap-1">
                  No Bid
                  {draftCount > 1 && (
                    <ArrowLeftIcon className="size-3 rotate-180" />
                  )}
                </div>
              </Button>
              <Button
                data-tooltip-id={tooltipId}
                primary
                disabled={operationsWithError.length > 0 || isPending}
                loading={activeButton === "complete" && isPending}
                onClick={() => {
                  setActiveButton("complete");
                  if (areAllChildEstimatesCompletable(part)) {
                    showModal(
                      <AutoCompleteSubPartsModal
                        partId={part.id}
                        cancel={closeModal}
                        done={(partIdsToSkip) => {
                          setActiveButton(null);
                          toNextDraftPart(partIdsToSkip);
                          closeModal();
                        }}
                      />,
                    );
                  } else {
                    updatePart({
                      payload: { will_bid: true },
                      onSuccess: () => {
                        setActiveButton(null);
                        toNextDraftPart();
                      },
                    });
                  }
                }}
              >
                <div className="flex items-center gap-1">
                  Complete
                  {draftCount > 1 && (
                    <ArrowLeftIcon className="size-3 rotate-180" />
                  )}
                </div>
              </Button>
              {operationsWithError.length > 0 && (
                <Tooltip id={tooltipId}>
                  <div className="text-red-200 flex flex-col text-xs">
                    <div className="">Error in operations:</div>
                    {operationsWithError.map(({ name, id }) => (
                      <div key={id}>{name}</div>
                    ))}
                  </div>
                </Tooltip>
              )}
            </>
          ) : (
            <Button
              loading={isPending}
              onClick={() =>
                updatePart({
                  payload: { will_bid: null },
                })
              }
            >
              Edit
            </Button>
          )}
          {readyToQuote(quoteRevision) && (
            <Button
              primary
              loading={isPending}
              onClick={() =>
                confirmCostEstimates(
                  {
                    quoteRevisionId: quoteRevision.id,
                  },
                  { onSuccess: () => toQuote() },
                )
              }
            >
              Quote{" >"}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  function toNextDraftPart(partIdsToSkip: string[] = []) {
    const id = nextDraftPartId(partIndex, parts, partIdsToSkip);
    if (id) toPart(id);
  }

  function toNextPart() {
    const nextPartIndex = partIndex === parts.length - 1 ? 0 : partIndex + 1;
    toPart(parts[nextPartIndex].id);
  }

  function toPreviousPart() {
    const nextPartIndex = partIndex === 0 ? parts.length - 1 : partIndex - 1;
    toPart(parts[nextPartIndex].id);
  }
}

function nextDraftPartId(
  currentPartIndex: number,
  parts: RequestedPartDetail[],
  partIdsToSkip: string[],
): string | null {
  const partsReordered = [
    ...parts.slice(currentPartIndex + 1),
    ...parts.slice(0, currentPartIndex),
  ];
  for (const part of partsReordered) {
    if (
      part.will_bid === null &&
      !part.is_ots &&
      !partIdsToSkip.includes(part.id)
    )
      return part.id;
  }
  return null;
}

function willBidToText(willBid: boolean | null) {
  if (willBid === true) return "Estimated";
  else if (willBid === false) return "No Bid";
  else return "Draft";
}
